/**
 * Referral timeline — server function.
 *
 * Returns the caller's outbound referrals (rows where they are the
 * referrer) with per-referral timeline data:
 *
 *   1. Invited          — referral row created (link captured at signup)
 *   2. Signed up        — attributed_at (friend created a TruWe account)
 *   3. Placed 1st order — qualified_at (friend's first paid order landed)
 *   4. Credited         — rewarded_at (TruCoins credited to referrer)
 *
 * RLS on `referrals` already scopes SELECT to
 *   referrer_customer_id IN (select id from customers where auth_user_id = auth.uid())
 * so we can call this with the caller's Supabase client (no service role).
 *
 * The referred user's identity is masked (first name / initials + last-4 of
 * phone) so the referrer only sees enough to recognize them.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ReferralTimelineStatus =
  | "attributed"
  | "qualified"
  | "rewarded"
  | "reversed"
  | "rejected"
  | "pending_review";

export type ReferralTimelineItem = {
  id: string;
  status: ReferralTimelineStatus;
  referralCode: string | null;
  displayName: string;         // masked
  displayHint: string | null;  // e.g. "•• 4321" phone tail (nullable)
  invitedAt: string | null;    // referral row created
  signedUpAt: string | null;   // attributed_at
  firstOrderAt: string | null; // qualified_at
  creditedAt: string | null;   // rewarded_at
  reversedAt: string | null;
  reviewReason: string | null;
  rewardPaise: number;         // referrer_reward_paise
};

export type ReferralTimelineResponse = { items: ReferralTimelineItem[] };

function maskName(name: string | null | undefined, phone: string | null | undefined, email: string | null | undefined) {
  const trimmed = (name ?? "").trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/);
    const first = parts[0];
    const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return lastInitial ? `${first} ${lastInitial.toUpperCase()}.` : first;
  }
  if (email) {
    const [local] = email.split("@");
    if (local) return `${local.slice(0, 2)}••••`;
  }
  if (phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length >= 4) return `Friend ••${digits.slice(-4)}`;
  }
  return "A friend";
}

function phoneTail(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return null;
  return `•• ${digits.slice(-4)}`;
}

export const getMyReferralTimeline = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ items: ReferralTimelineItem[] }> => {
    const { supabase, userId } = context;

    const { data: me, error: meErr } = await supabase
      .from("customers")
      .select("id")
      .eq("auth_user_id", userId)
      .maybeSingle();
    if (meErr) throw new Error(meErr.message);
    if (!me?.id) return { items: [] };

    const { data: rows, error } = await supabase
      .from("referrals")
      .select(
        "id, status, referral_code, referrer_reward_paise, attributed_at, qualified_at, rewarded_at, reversed_at, review_reason, created_at, referred_customer_id",
      )
      .eq("referrer_customer_id", me.id)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) throw new Error(error.message);
    if (!rows || rows.length === 0) return { items: [] };

    // Fetch referred customer minimal display fields via service role (referrer
    // does NOT have RLS to read other customers' rows directly).
    const referredIds = Array.from(
      new Set(rows.map((r) => r.referred_customer_id).filter((v): v is string => !!v)),
    );

    let refMap = new Map<string, { name: string | null; phone: string | null; email: string | null }>();
    if (referredIds.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: refs, error: rerr } = await supabaseAdmin
        .from("customers")
        .select("id, name, phone, email")
        .in("id", referredIds);
      if (rerr) throw new Error(rerr.message);
      refMap = new Map((refs ?? []).map((c) => [c.id, { name: c.name, phone: c.phone, email: c.email }]));
    }

    const items: ReferralTimelineItem[] = rows.map((r) => {
      const person = r.referred_customer_id ? refMap.get(r.referred_customer_id) : undefined;
      return {
        id: r.id,
        status: r.status as ReferralTimelineStatus,
        referralCode: r.referral_code,
        displayName: maskName(person?.name, person?.phone, person?.email),
        displayHint: phoneTail(person?.phone),
        invitedAt: r.created_at,
        signedUpAt: r.attributed_at,
        firstOrderAt: r.qualified_at,
        creditedAt: r.rewarded_at,
        reversedAt: r.reversed_at,
        reviewReason: r.review_reason,
        rewardPaise: r.referrer_reward_paise ?? 0,
      };
    });

    return { items };
  });
